import { computed, toValue } from 'vue';
import type { MaybeRefOrGetter } from 'vue';
import type { Seo } from '~/types/strapi';

export const useStrapiSeo = (seoData?: MaybeRefOrGetter<Seo | null | undefined>) => {
  const seo = computed(() => toValue(seoData));

  // Register SEO meta synchronously in setup to keep Nuxt context
  useSeoMeta(() => {
    const data = seo.value;
    if (!data) return {};

    return {
      title: data.metaTitle,
      description: data.metaDescription,
      keywords: data.keywords,
      robots: data.metaRobots,
      ogTitle: data.openGraph?.ogTitle ?? data.metaTitle,
      ogDescription: data.openGraph?.ogDescription ?? data.metaDescription,
      ogImage: data.openGraph?.ogImage?.url ?? data.metaImage?.url,
      ogUrl: data.openGraph?.ogUrl,
      ogType: data.openGraph?.ogType,
      ogLocale: data.openGraph?.ogLocale,
      ogImageAlt: data.openGraph?.ogImage?.alternativeText ?? data.metaImage?.alternativeText,
      ogImageWidth: data.openGraph?.ogImage?.width ?? data.metaImage?.width,
      ogImageHeight: data.openGraph?.ogImage?.height ?? data.metaImage?.height,
    };
  });

  useHead(() => {
    const data = seo.value;
    if (!data) return {};

    const meta: Array<Record<string, unknown>> = [];
    const link: Array<Record<string, unknown>> = [];
    const script: Array<Record<string, unknown>> = [];

    if (data.metaViewport) {
      meta.push({ name: 'viewport', content: data.metaViewport });
    }

    if (data.canonicalURL) {
      link.push({ rel: 'canonical', href: data.canonicalURL });
    }

    if (data.structuredData) {
      const structuredDataArray = Array.isArray(data.structuredData)
        ? data.structuredData
        : [data.structuredData];

      structuredDataArray.forEach((item, index) => {
        if (!item) return;

        script.push({
          type: 'application/ld+json',
          innerHTML: typeof item === 'string' ? item : JSON.stringify(item),
          key: `structured-data-${index}`,
        });
      });
    }

    return {
      meta,
      link,
      script,
    };
  });
};